import { useEffect, useMemo, useState } from 'react'

import AreaRecruitmentPanel from './AreaRecruitmentPanel'
import type { CityGroup } from './AreaRecruitmentPanel'
import JapanMap from './JapanMap'
import {
  AREA_DATA,
  computeRecruitmentArea,
  getPrefecturePercentage,
  getRecruitmentProgress,
} from './utils'
import RecruitmentProgress from './RecruitmentProgress'
import styles from './styles.module.scss'

const MapSection = (): JSX.Element => {
  const [selectedPrefecture, setSelectedPrefecture] = useState('東京都')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)')
    const updateMobileState = () => setIsMobile(mediaQuery.matches)
    updateMobileState()
    mediaQuery.addEventListener('change', updateMobileState)
    return () => mediaQuery.removeEventListener('change', updateMobileState)
  }, [])

  const nationalProgress = useMemo(() => getRecruitmentProgress(AREA_DATA), [])
  const activeAreaCount = useMemo(
    () =>
      Object.values(AREA_DATA).reduce(
        (total, areas) =>
          total + areas.filter((area) => computeRecruitmentArea(area).remaining > 0).length,
        0
      ),
    []
  )
  const prefectureCount = Object.keys(AREA_DATA).length
  const selectedAreas = useMemo(
    () =>
      (AREA_DATA[selectedPrefecture] ?? [])
        .map(computeRecruitmentArea)
        .sort((first, second) => second.remaining - first.remaining),
    [selectedPrefecture]
  )
  const cityGroups = useMemo<CityGroup[]>(() => {
    const groups = selectedAreas.reduce<Record<string, typeof selectedAreas>>((result, area) => {
      const city = area.city || 'その他エリア'
      result[city] = result[city] ?? []
      result[city].push(area)
      return result
    }, {})
    return Object.entries(groups)
      .map(([city, areas]) => ({
        city,
        areas,
        remaining: areas.reduce((total, area) => total + area.remaining, 0),
        percentage: getPrefecturePercentage(areas),
      }))
      .sort((first, second) => second.remaining - first.remaining)
  }, [selectedAreas])

  return (
    <section className={`${styles.block} ${styles.mapBlock}`} id="map">
      <div className={`${styles.content} ${styles.center}`}>
        <span className={`${styles.eyebrow} ${styles.reveal}`} data-reveal="">
          いま足りないエリア
        </span>
        <h2 className={`${styles.sectionTitle} ${styles.reveal}`} data-reveal="">
          日本地図から、あなたの都道府県を選ぶ
        </h2>
        <p className={`${styles.sectionSubtitle} ${styles.reveal}`} data-reveal="">
          色がついているエリアほど、荷物を預けられる場所が足りていません。タイルをタップすると、募集中の駅・施設が表示されます。
        </p>
        <RecruitmentProgress
          progress={nationalProgress}
          activeAreaCount={activeAreaCount}
          prefectureCount={prefectureCount}
        />
      </div>
      <div className={`${styles.content} ${styles.mapLayout} ${styles.reveal}`} data-reveal="">
        <JapanMap
          selectedPrefecture={selectedPrefecture}
          isMobile={isMobile}
          onSelect={setSelectedPrefecture}
        />
        <AreaRecruitmentPanel
          prefecture={selectedPrefecture}
          areas={selectedAreas}
          cityGroups={cityGroups}
          percentage={getPrefecturePercentage(AREA_DATA[selectedPrefecture] ?? [])}
          activeAreaCount={selectedAreas.filter((area) => area.remaining > 0).length}
          groupByCity={cityGroups.length >= 2 && selectedAreas.length >= 10}
        />
      </div>
      <div className={`${styles.content}`}>
        <div className={`${styles.middleCta} ${styles.reveal}`} data-reveal="">
          <div>
            <b>紹介したいお店が浮かびましたか？</b>
            <span>
              紹介は1分で完了。掲載開始で紹介者に15,000円、導入店舗に10,000円をお支払いします。
            </span>
          </div>
          <a href="#form" className={`${styles.button} ${styles.primaryButton}`}>
            紹介者登録をする
          </a>
        </div>
      </div>
    </section>
  )
}

export default MapSection
